import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { DataStats } from "@/components/data-stats/DataStats";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { listSkills, PaginatedSkillsResponse } from "@/services/skillsApi";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

type SortBy = "name" | "candidate_count";

function SortHeader({
  label,
  field,
  current,
  order,
  onSort,
}: {
  label: string;
  field: SortBy;
  current: SortBy;
  order: string;
  onSort: (field: SortBy) => void;
}) {
  const active = current === field;
  return (
    <th
      className="px-3 py-2 text-left cursor-pointer select-none hover:bg-gray-100"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {active ? (
          order === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
        )}
      </div>
    </th>
  );
}

export default function SkillsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PaginatedSkillsResponse | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [sortBy, setSortBy] = useState<SortBy>("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listSkills(navigate, { page, size, sort_by: sortBy, sort_order: sortOrder });
        setData(res);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, page, size, sortBy, sortOrder]);

  const handleSort = (field: SortBy) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder(field === "name" ? "asc" : "desc");
    }
    setPage(1);
  };

  const canPrev = page > 1;
  const canNext = data ? page < data.pages : false;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Competences</h1>
          </div>
        </header>

        <div className="p-6 space-y-4">
          <DataStats entity="skills" />
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Liste des competences</h2>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Taille page</label>
              <select
                className="border rounded px-2 py-1 text-sm"
                value={size}
                onChange={(e) => {
                  setPage(1);
                  setSize(parseInt(e.target.value, 10));
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}
          {loading && <div className="text-gray-500 text-sm">Chargement...</div>}

          <div className="overflow-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Id</th>
                  <SortHeader label="Nom" field="name" current={sortBy} order={sortOrder} onSort={handleSort} />
                  <th className="px-3 py-2 text-left">External ref</th>
                  <SortHeader label="Candidats" field="candidate_count" current={sortBy} order={sortOrder} onSort={handleSort} />
                </tr>
              </thead>
              <tbody>
                {data?.items?.map((s) => (
                  <tr key={s.id} className="border-t hover:bg-muted/50">
                    <td className="px-3 py-2">{s.id}</td>
                    <td className="px-3 py-2 font-medium">{s.name}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 font-mono">{s.external_ref ?? "—"}</td>
                    <td className="px-3 py-2">{s.candidate_count}</td>
                  </tr>
                ))}
                {!data?.items?.length && !loading && (
                  <tr>
                    <td className="px-3 py-4 text-center text-gray-500" colSpan={4}>Aucune competence</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {data?.page ?? page} / {data?.pages ?? 1} — {data?.total ?? 0} elements
            </div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={!canPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Precedent
              </Button>
              <Button variant="outline" disabled={!canNext} onClick={() => setPage((p) => p + 1)}>
                Suivant
              </Button>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
